/**
 * TagEditor — find-or-create tag input with autocomplete.
 *
 * Displays current tags as removable chips. A text input shows an autocomplete
 * dropdown filtered against the vocabulary. Typing a new name and pressing
 * Enter (or clicking "Add ...") creates it inline. Changes are communicated
 * via `onChange(tagNames)` — the parent calls the mutation.
 */

import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { X, Tag } from "lucide-react";

export interface TagItem {
  id: string;
  name: string;
  slug: string;
}

interface TagEditorProps {
  /** Currently assigned tags. */
  value: TagItem[];
  /** Full vocabulary (all existing tags). */
  vocabulary: TagItem[];
  /** Called with the full list of tag names whenever the set changes. */
  onChange: (tagNames: string[]) => void;
  /** Disable editing (read-only chip display). */
  disabled?: boolean;
  /** Placeholder text for the input. */
  placeholder?: string;
}

export function TagEditor({
  value,
  vocabulary,
  onChange,
  disabled = false,
  placeholder = "Add a tag...",
}: TagEditorProps) {
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Currently assigned slugs for quick lookup
  const assignedSlugs = useMemo(
    () => new Set(value.map((t) => t.slug)),
    [value],
  );

  // Filter vocabulary: exclude already-assigned, match input
  const suggestions = useMemo(() => {
    const trimmed = input.trim().toLowerCase();
    return vocabulary.filter(
      (t) => !assignedSlugs.has(t.slug) && t.name.toLowerCase().includes(trimmed),
    );
  }, [vocabulary, assignedSlugs, input]);

  // Whether the current input would create a new tag (not in vocabulary)
  const isNew = useMemo(() => {
    const trimmed = input.trim().toLowerCase();
    if (!trimmed) return false;
    return !vocabulary.some((t) => t.name.toLowerCase() === trimmed);
  }, [input, vocabulary]);

  // Combined options: matching vocabulary + optional "create new" entry
  const options = useMemo(() => {
    const list: { label: string; value: string; isCreate: boolean }[] =
      suggestions.map((t) => ({ label: t.name, value: t.name, isCreate: false }));
    if (isNew && input.trim()) {
      list.push({
        label: `Add "${input.trim()}"`,
        value: input.trim(),
        isCreate: true,
      });
    }
    return list;
  }, [suggestions, isNew, input]);

  // Reset highlight when options change
  useEffect(() => {
    setHighlightIdx(-1);
  }, [options.length]);

  const addTag = useCallback(
    (name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      // Avoid duplicates (case-insensitive)
      const already = value.some(
        (t) => t.name.toLowerCase() === trimmed.toLowerCase(),
      );
      if (already) return;
      onChange([...value.map((t) => t.name), trimmed]);
      setInput("");
      setOpen(false);
      inputRef.current?.focus();
    },
    [value, onChange],
  );

  const removeTag = useCallback(
    (slug: string) => {
      onChange(value.filter((t) => t.slug !== slug).map((t) => t.name));
    },
    [value, onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIdx((i) => Math.min(i + 1, options.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (highlightIdx >= 0 && highlightIdx < options.length) {
          addTag(options[highlightIdx].value);
        } else if (input.trim()) {
          addTag(input.trim());
        }
      } else if (e.key === "Escape") {
        setOpen(false);
        setInput("");
      } else if (e.key === "Backspace" && !input && value.length > 0) {
        // Remove last tag on backspace with empty input
        removeTag(value[value.length - 1].slug);
      }
    },
    [options, highlightIdx, input, value, addTag, removeTag],
  );

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      {/* Chip row + input */}
      <div
        className={`flex flex-wrap items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-paper hair border text-[13px] transition ${
          open ? "border-tide" : ""
        } ${disabled ? "opacity-60 pointer-events-none" : ""}`}
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((tag) => (
          <span
            key={tag.slug}
            className="inline-flex items-center gap-1 bg-paper-2 text-ink-2 rounded px-2 py-0.5 text-[12px] font-medium"
          >
            {tag.name}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeTag(tag.slug);
                }}
                className="text-ink-4 hover:text-ink-2 -mr-0.5 cursor-pointer"
                aria-label={`Remove ${tag.name}`}
              >
                <X size={10} strokeWidth={2.5} />
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={value.length === 0 ? placeholder : ""}
            className="flex-1 min-w-[80px] bg-transparent outline-none text-ink placeholder:text-ink-5 text-[12.5px] py-0.5"
          />
        )}
      </div>

      {/* Dropdown */}
      {open && options.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-paper-hi rounded-md shadow-lift hair border max-h-48 overflow-y-auto">
          {options.map((opt, idx) => (
            <button
              key={opt.value + (opt.isCreate ? "-new" : "")}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault(); // prevent input blur
                addTag(opt.value);
              }}
              onMouseEnter={() => setHighlightIdx(idx)}
              className={`w-full text-left px-3 py-2 text-[12.5px] flex items-center gap-2 ${
                idx === highlightIdx
                  ? "bg-tide-ss text-ink"
                  : "text-ink-2 hover:bg-canvas"
              }`}
            >
              {opt.isCreate ? (
                <>
                  <Tag size={12} strokeWidth={2} className="text-tide flex-shrink-0" />
                  <span>{opt.label}</span>
                </>
              ) : (
                <span>{opt.label}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Read-only tag chips — used on list rows and read-mode profile.
 */
export function TagChips({ tags }: { tags: TagItem[] }) {
  if (tags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map((tag) => (
        <span
          key={tag.slug}
          className="inline-flex items-center bg-paper-2 text-ink-3 rounded px-1.5 py-0.5 text-[10.5px] font-medium"
        >
          {tag.name}
        </span>
      ))}
    </div>
  );
}
